/**
 * Fuso applicativo `Europe/Rome`. Giornata, turno e termini di chiusura si calcolano qui,
 * mai nel client: la conversione è responsabilità del livello applicativo (§ 2).
 *
 * I timestamp restano in ISO 8601 UTC; solo la lettura «che giorno è» e «che ora è» passa
 * per il fuso italiano, che alterna CET e CEST.
 */

export const FUSO = 'Europe/Rome';

const FORMATO_GIORNO = new Intl.DateTimeFormat('en-CA', {
  timeZone: FUSO,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const FORMATO_ORA = new Intl.DateTimeFormat('en-GB', {
  timeZone: FUSO,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** Giornata in `Europe/Rome` nel formato `YYYY-MM-DD`. */
export function giornata(momento: Date = new Date()): string {
  return FORMATO_GIORNO.format(momento);
}

/** Ora locale in `Europe/Rome` nel formato `HH:MM`. */
export function oraLocale(momento: Date = new Date()): string {
  return FORMATO_ORA.format(momento);
}

/** Minuti dalla mezzanotte in `Europe/Rome`. Comodo per confrontare con `turno_orario`. */
export function minutiDaMezzanotte(momento: Date = new Date()): number {
  return inMinuti(oraLocale(momento));
}

/** `HH:MM` → minuti dalla mezzanotte. */
export function inMinuti(oraHHMM: string): number {
  const [ore, minuti] = oraHHMM.split(':').map(Number);
  return (ore ?? 0) * 60 + (minuti ?? 0);
}

/** Timestamp ISO 8601 UTC, la forma in cui tutto viene memorizzato. */
export function adesso(): string {
  return new Date().toISOString();
}

/** Somma ore a un istante e restituisce l'ISO UTC. */
export function fraOre(ore: number, da: Date = new Date()): string {
  return new Date(da.getTime() + ore * 3_600_000).toISOString();
}

/** Minuti trascorsi fra due istanti ISO. */
export function minutiTrascorsi(daIso: string, a: Date = new Date()): number {
  return (a.getTime() - new Date(daIso).getTime()) / 60_000;
}

/** Vero se l'istante ISO è già passato. */
export function scaduto(iso: string, adessoData: Date = new Date()): boolean {
  return new Date(iso).getTime() <= adessoData.getTime();
}

/**
 * Confronto fra due date `YYYY-MM-DD`. Il confronto lessicografico basta e non introduce
 * ambiguità di fuso: sono già giornate italiane, non istanti.
 */
export function giornoNonAnteriore(data: string, riferimento: string): boolean {
  return data >= riferimento;
}

/** Somma giorni a una data `YYYY-MM-DD`. Aritmetica su giornate, non su istanti. */
export function giornoPiu(data: string, giorni: number): string {
  const [a, m, g] = data.split('-').map(Number);
  const d = new Date(Date.UTC(a ?? 0, (m ?? 1) - 1, (g ?? 1) + giorni));
  return d.toISOString().slice(0, 10);
}

/**
 * Un anno di calendario dopo una data `AAAA-MM-GG`.
 *
 * Non 365 giorni: attraversando un 29 febbraio, sommare i giorni fa cadere la scadenza il
 * giorno prima dell'anniversario. Un socio rinnovato il 1° marzo 2027 si sarebbe visto
 * scadere l'iscrizione il 29 febbraio 2028 invece del 1° marzo — un giorno regalato, contro
 * la promessa scritta accanto al rinnovo: «non deve regalare mesi né toglierne».
 *
 * Il 29 febbraio stesso non ha un anniversario: `Date.UTC(2029, 1, 29)` scivola da sé al
 * 1° marzo, che è la convenzione ordinaria e quella che una segreteria si aspetta.
 */
export function annoDopo(data: string): string {
  const [a, m, g] = data.split('-').map(Number);
  return new Date(Date.UTC((a ?? 0) + 1, (m ?? 1) - 1, g ?? 1)).toISOString().slice(0, 10);
}

/**
 * Giorno della settimana di una data `YYYY-MM-DD`: 0 lunedì … 6 domenica.
 *
 * L'ordine è quello italiano, non quello di `getDay()`, che parte da domenica. Il calcolo
 * passa per UTC perché la stringa è una giornata di calendario, non un istante: leggerla
 * nel fuso locale la sposterebbe di un giorno per chi sta a ovest di Greenwich.
 */
export function giornoSettimana(data: string): number {
  const [a, m, g] = data.split('-').map(Number);
  return (new Date(Date.UTC(a ?? 0, (m ?? 1) - 1, g ?? 1)).getUTCDay() + 6) % 7;
}

/** Lunedì della settimana che contiene la data. */
export function lunediDi(data: string): string {
  return giornoPiu(data, -giornoSettimana(data));
}

const PARTI_ROMA = new Intl.DateTimeFormat('en-US', {
  timeZone: FUSO,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/** Scarto in minuti fra l'ora di Roma e UTC in un dato istante: +60 (CET) o +120 (CEST). */
function scartoRoma(istante: Date): number {
  const parti = PARTI_ROMA.formatToParts(istante);
  const n = (tipo: string) => Number(parti.find((p) => p.type === tipo)?.value ?? 0);
  const comeUtc = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour') % 24, n('minute'), n('second'));
  return (comeUtc - istante.getTime()) / 60_000;
}

/**
 * Istante UTC corrispondente a un'ora di calendario italiana.
 * Serve ai termini fissati sull'orologio locale: le 23:59 del giorno precedente (R-01),
 * la fine di una fascia oraria (R-13).
 *
 * Lo scarto si applica due volte perché nei giorni del cambio d'ora la prima stima può
 * cadere dalla parte sbagliata del passaggio.
 */
export function istanteRoma(giorno: string, oraHHMM: string): Date {
  const [a, m, g] = giorno.split('-').map(Number);
  const [h, min] = oraHHMM.split(':').map(Number);
  const ingenuo = Date.UTC(a ?? 0, (m ?? 1) - 1, g ?? 1, h ?? 0, min ?? 0);

  const primo = new Date(ingenuo - scartoRoma(new Date(ingenuo)) * 60_000);
  const scarto = scartoRoma(primo);
  return new Date(ingenuo - scarto * 60_000);
}
